package ai.wagemix.logic.marketing

import ai.wagemix.data.touchesUser
import ai.wagemix.logic.marketing.WMMarketingChromePresenter.Action
import ai.wagemix.logic.marketing.WMMarketingChromePresenter.Event
import ai.wagemix.logic.session
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.WMRoutes
import ai.wagemix.net.models.WMSession
import com.fsryan.foundation.logic.FSBasePresenter
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.FSPresenter.Event.NullableValueWrapper
import com.fsryan.foundation.logic.NullableValueWrapper
import com.fsryan.foundation.models.FSPropertyContainer
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.map

/**
 * The marketing chrome around the logged-out pages: whether a visitor is signed in (so the header
 * offers "Open app" instead of Log in / Create account), opening the app, and, on the landing
 * page, sending a signed-in visitor on to the mix builder. One instance per layout composition.
 */
interface WMMarketingChromePresenter<out E: Event, in A: Action>: FSPresenter<E, A> {

    interface Action: FSPresenter.Action {
        sealed interface Base: Action

        @ConsistentCopyVisibility
        data class OpenApp internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<OpenApp> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        interface Factory<out A: Action>: FSPresenter.Action.Factory<A> {
            fun createOpenApp(extendedProperties: Map<String, Any> = emptyMap()): A
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                override fun createOpenApp(extendedProperties: Map<String, Any>): Base = OpenApp(extendedProperties)
            }
        }
    }

    interface Event: FSPresenter.Event {
        /** The signed-in session, or a wrapped `null` for a visitor. `null` means no change. */
        val sessionUpdate: NullableValueWrapper<WMSession?>?
        val navigationUpdate: WMNavigationRequest?

        @ConsistentCopyVisibility
        data class Base internal constructor(
            private val base: FSPresenter.Event.Base,
            override val sessionUpdate: NullableValueWrapper<WMSession?>? = null,
            override val navigationUpdate: WMNavigationRequest? = null
        ): Event, FSPropertyContainer<Base>, FSPresenter.Event by base {
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<out E: Event>: FSPresenter.Event.Factory<E> {
            fun createSessionUpdate(session: WMSession?): E
            fun createNavigation(request: WMNavigationRequest): E
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                private val base = FSPresenter.Event.baseFactory()
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createSessionUpdate(session: WMSession?) = Base(base.none(), sessionUpdate = NullableValueWrapper(session))
                override fun createNavigation(request: WMNavigationRequest) = Base(base.none(), navigationUpdate = request)
            }
        }
    }

    fun userRequestedOpenApp(extendedProperties: Map<String, Any> = emptyMap())

    companion object
}

abstract class WMBaseMarketingChromePresenter<
    E: Event,
    A: Action,
    EF: Event.Factory<E>,
    AF: Action.Factory<A>,
    G: WMMarketingChromeDataGateway
>(
    /** `true` on the landing page: a signed-in visitor is sent on to the mix builder once the session is known. */
    protected val leaveWhenSignedIn: Boolean,
    protected val dataGateway: G,
    eventFactory: EF,
    protected val actionFactory: AF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String
): FSBasePresenter<E, A, EF>(cancelViewScopeOnUnready, instanceId, eventFactory), WMMarketingChromePresenter<E, A> {

    override fun userRequestedOpenApp(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createOpenApp(extendedProperties))

    override fun registerSubscriptions() {
        dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .map { dataGateway.session() }
            .distinctUntilChanged()
            .map { session -> emitSession(session) }
            .launchInViewScope()
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) {
        emitSession(dataGateway.session())
    }

    override fun onUserAction(action: A) {
        logUserAction(action)
        when (action) {
            is Action.OpenApp -> launch { eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.MIX)).emit() }
            else -> logUnknownActionType(action)
        }
    }

    private suspend fun emitSession(session: WMSession?) {
        eventFactory.createSessionUpdate(session).emit()
        if (leaveWhenSignedIn && session != null) {
            eventFactory.createNavigation(WMNavigationRequest.Route(WMRoutes.MIX)).emit()
        }
    }
}

typealias WMDefaultMarketingChromePresenter = WMMarketingChromePresenter<Event.Base, Action.Base>

fun WMMarketingChromePresenter.Companion.createDefault(
    leaveWhenSignedIn: Boolean,
    dataGateway: WMMarketingChromeDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMMarketingChromePresenter")
): WMDefaultMarketingChromePresenter = object: WMBaseMarketingChromePresenter<
    Event.Base,
    Action.Base,
    Event.Factory<Event.Base>,
    Action.Factory<Action.Base>,
    WMMarketingChromeDataGateway
>(
    leaveWhenSignedIn = leaveWhenSignedIn,
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    actionFactory = Action.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId
) {}
